import { useEffect, useCallback, useState, useContext } from "react";
import { IMoreOption, ITransactionalInfo } from "src/types/molecules";
import TransactionalInfoUI from "./TransactionalInfoUI";
import { transactionService } from "@services/TransactionService";
import { clientService } from "@services/ClientService";
import { IClientSummary, IReceiptName, ITransaction, ITransactionReceipt } from "src/types/global";
import { useForm } from "react-hook-form";
import { getCleanCurrency, getCurrency, getCurrentDate, getNumberCurrency } from "@utils/utils";
import { receiptService } from "@services/ReceiptService";
import { authService } from "@services/authService";
import { GlobalAlertContext } from "@providers/GlobalAlertProvider";
import { useNavigate } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import PDFContent from "../PDFContent/PDFContent";
const TransactionalInfo = ({
  receipt,
  setReceipt,
  clientId
}: ITransactionalInfo) => {
  const navigate = useNavigate();
  const receiptId = Number(receipt.id);
  const [status, setStatus] = useState(false);
  const [lastTransactionTime, setLastTransactionTime] = useState(receipt.lastTransactionTime);
  const [summary, setSummary] = useState<ITransactionReceipt>({
    receiptTotal: 0,
    returnTotal: 0,
    totalPaid: 0
  });
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Hubo un error transaccional");
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [showCloserDialog, setShowCloserDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clientSummary, setClientSummary] = useState<IClientSummary | null>(null);
  const [employeeCode, setEmployeeCode] = useState("");
  const [applicant, setApplicant] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(0);
  const [transferProof, setTransferProof] = useState<File | null>(null);
  const {
    setGlobalAlertMessage,
    setGlobalAlertSeverity,
    showGlobalAlert,
    hiddenGlobalAlert
  } = useContext(GlobalAlertContext);
  const {
    register,
    setValue,
    handleSubmit,
    control
  } = useForm<ITransaction>();
  const {
    register: registerName,
    setValue: setNameValue,
    handleSubmit: handleName
  } = useForm<IReceiptName>();
  const handleClosePayDialog = () => {
    setShowPayDialog(false);
    setEmployeeCode("");
    setApplicant("");
    setPaymentMethod(0);
    setTransferProof(null);
    setValue("amount", "");
    setValue("employeeCode", "");
    setValue("applicant", "");
  };
  const pendingAmount = summary.receiptTotal - summary.totalPaid - summary.returnTotal;
  const [isPayFull, setIsPayFull] = useState(false);
  const options: IMoreOption[] = [{
    show: status && receipt.type !== 1,
    primary: "Agregar productos",
    path: `/recibos/${receipt.id}/productos`,
    state: {
      ...receipt,
      requestProducts: [],
      returnProducts: []
    }
  }, {
    show: status,
    primary: "Cerrar recibo",
    onClick: () => setShowCloserDialog(true)
  }, {
    // show: authService.isAdmin(),
    show: true,
    primary: "Eliminar",
    onClick: () => setShowDeleteDialog(true)
  }, {
    show: true,
    primary: "Imprimir",
    onClick: async () => await printReceipt()
  }, {
    show: true,
    primary: "Editar nombre",
    onClick: () => setShowNameDialog(true)
  }];
  const pay = async (data: ITransaction) => {
    try {
      setIsLoading(true);
      if (!employeeCode) {
        throw new Error("El código de empleado es requerido");
      }
      hiddenGlobalAlert();
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("Pago registrado");
      if (paymentMethod === 1 && !transferProof) {
        throw new Error("Se requiere el comprobante de transferencia");
      }
      const transaction: ITransaction = {
        clientId,
        amount: getNumberCurrency(String(data.amount)),
        employeeCode,
        applicant: data.applicant,
        transactionType: 0,
        receiptId,
        paymentMethod,
        transferProof: transferProof ?? undefined
      };
      await transactionService.create(transaction);
      await getReceipt();
      showGlobalAlert();
      setShowPayDialog(false);
      setEmployeeCode("");
      setApplicant("");
      setPaymentMethod(0);
      setTransferProof(null);
      setValue("amount", "");
      setValue("employeeCode", "");
      setValue("applicant", "");
      setLastTransactionTime(getCurrentDate());
    } catch (error) {
      setErrorMessage("Lo sentimos, no se pudo registrar el pago");
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };
  const editName = async (data: IReceiptName) => {
    try {
      setIsLoading(true);
      data.receiptId = receiptId;
      await receiptService.putName(data);
      setReceipt({
        ...receipt,
        name: data.name.toLocaleLowerCase()
      });
    } catch (error) {
      setErrorMessage("Error, inténtalo de nuevo");
      setShowError(true);
    } finally {
      setShowNameDialog(false);
      setIsLoading(false);
    }
  };
  const close = async () => {
    try {
      setIsLoading(true);
      hiddenGlobalAlert();
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("Recibo cerrado");
      const res = await receiptService.close(receiptId, employeeCode, applicant);
      if (!res.data.success) throw new Error(res.data.message);
      await getReceipt();
      setReceipt({
        ...receipt,
        status: false
      });
      showGlobalAlert();
    } catch (error: any) {
      if (error?.message?.includes("zero")) {
        setErrorMessage("Error, el recibo no debe tener saldo pendiente");
      } else {
        setErrorMessage("Error, hubo un error");
      }
      setShowError(true);
    } finally {
      setIsLoading(false);
      setShowCloserDialog(false);
      setEmployeeCode("");
    }
  };
  const deleteReceipt = async () => {
    try {
      setIsLoading(true);
      if (!employeeCode) {
        throw new Error();
      }
      hiddenGlobalAlert();
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("!Listo! El recibo ha sido eliminado correctamente.");
      await receiptService.remove(receiptId, employeeCode);
      navigate("/recibos", {
        state: {
          showAlert: true
        }
      });
      showGlobalAlert();
    } catch (error) {
      setErrorMessage("Error, intentalo de nuevo");
      setShowError(true);
    } finally {
      setIsLoading(false);
      setEmployeeCode("");
      setValue("employeeCode", "");
    }
  };
  const checkPending = (value: string) => {
    const numberValue = getNumberCurrency(value);
    if (numberValue > summary.receiptTotal) {
      setValue("amount", getCleanCurrency(summary.receiptTotal.toString()));
    }
  };
  const onTotalPay = (value: boolean) => {
    setValue("amount", value ? getCleanCurrency(pendingAmount) : "");
    setIsPayFull(value);
  };
  const printReceipt = async () => {
    const component = PDFContent(receipt.id.toString(), receipt.clientName, receipt.clientIdentification, receipt.clientPhone, status, receipt.creationTime, lastTransactionTime, getCurrency(summary.receiptTotal), getCurrency(summary.returnTotal * -1), getCurrency(receipt.type === 0 ? summary.totalPaid * -1 : 0), getCurrency(summary.receiptTotal - summary.returnTotal - Math.abs(summary.totalPaid)), receipt.requestProducts, receipt.returnProducts);
    let blobPdf = await pdf(component).toBlob();
    const pdfUrl = URL.createObjectURL(blobPdf);
    window.open(pdfUrl, "_blank");
    URL.revokeObjectURL(pdfUrl);
  };
  const getReceipt = useCallback(async () => {
    if (isNaN(receiptId)) {
      return;
    }
    try {
      const res = await transactionService.getReceipt(receiptId);
      const summaryDB = res.data;
      setSummary(summaryDB);
    } catch {
      setErrorMessage("Hubo un error transaccional");
      setShowError(true);
    }
  }, [receiptId]);
  useEffect(() => {
    if (!clientId || clientId < 0) return;
    let cancelled = false;
    clientService.getSummary(clientId).then(res => {
      if (!cancelled) setClientSummary(res.data);
    }).catch(() => {/* non-critical */});
    return () => {
      cancelled = true;
    };
  }, [clientId]);
  useEffect(() => {
    setStatus(receipt.status);
  }, [receipt.status]);
  useEffect(() => {
    setLastTransactionTime(receipt.lastTransactionTime);
  }, [receipt.lastTransactionTime]);
  useEffect(() => {
    if (typeof receipt.id === "number") {
      getReceipt();
    }
  }, [getReceipt, receipt]);
  useEffect(() => {
    if (!showPayDialog) {
      setValue("amount", "");
    }
  }, [showPayDialog, setValue]);
  useEffect(() => {
    if (!showNameDialog) {
      setNameValue("name", "");
      setNameValue("employeeCode", "");
    }
  }, [showNameDialog, setNameValue]);
  const handleCloseNameDialog = () => {
    setShowNameDialog(false);
    setNameValue("name", "");
    setNameValue("employeeCode", "");
  };
  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setEmployeeCode("");
    setValue("employeeCode", "");
  };
  return <TransactionalInfoUI receiptId={receipt.id} status={receipt.status} totalPaid={summary.totalPaid} receiptTotal={summary.receiptTotal} returnTotal={summary.returnTotal} showError={showError} closeError={() => setShowError(false)} errorMessage={errorMessage} showPayDialog={showPayDialog} setShowPayDialog={setShowPayDialog} showCloserDialog={showCloserDialog} setShowCloserDialog={setShowCloserDialog} showDeleteDialog={showDeleteDialog} setShowDeleteDialog={setShowDeleteDialog} showNameDialog={showNameDialog} setShowNameDialog={setShowNameDialog} register={register} control={control} pay={handleSubmit(pay)} registerName={registerName} editName={handleName(editName)} close={close} pending={getCurrency(pendingAmount)} payFull={isPayFull} checkPending={checkPending} onTotalPay={onTotalPay} isLoading={isLoading} moreOptions={options} deleteReceipt={deleteReceipt} isAdmin={authService.isAdmin()} employeeCode={employeeCode} setEmployeeCode={setEmployeeCode} applicant={applicant} setApplicant={setApplicant} handleClosePayDialog={handleClosePayDialog} handleCloseNameDialog={handleCloseNameDialog} handleCloseDeleteDialog={handleCloseDeleteDialog} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} transferProof={transferProof} setTransferProof={setTransferProof} clientSummary={clientSummary} />;
};
export default TransactionalInfo;